import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Card, CardFooter, CardHeader } from '@heroui/card';
import { Link } from '@heroui/link';
import { Image } from '@heroui/image';
import { Select, SelectItem } from '@heroui/select';

import Container from '@/components/Container/Container.tsx';
import PaperContainer from '@/components/PaperContainer/PaperContainer.tsx';
import songbookImage from '@/assets/images/songbook.jpg';
import progressionsImage from '@/assets/images/guitar-player.jpg';
import FrontPageLayout from '@/layouts/frontpage.tsx';
import Logo from '@/components/Logo/Logo.tsx';

function parseLanguage(lang: string) {
  switch (lang.split('-')[0]) {
    case 'nl':
      return 'nl';
    default:
      return 'en';
  }
}

function Opening() {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(parseLanguage(i18n.language));

  const items: {
    title: string;
    subtitle: string;
    image: string;
    href: string;
  }[] = [
    {
      title: t('opening.progressions.title'),
      subtitle: t('opening.progressions.subtitle'),
      href: '/progressions',
      image: progressionsImage,
    },
    {
      title: t('opening.songbook.title'),
      subtitle: t('opening.songbook.subtitle'),
      href: '/songbook',
      image: songbookImage,
    },
  ];

  return (
    <FrontPageLayout>
      <PaperContainer>
        <div className="bg-radial-[at_95%_10%] to-transparent from-primary-50/60 py-1 sm:py-12">
          <Container>
            <div className="flex justify-center my-12">
              <Logo className="h-20" />
            </div>
            <div className="flex gap-8 justify-center my-12">
              {items.map((i) => (
                <Card
                  key={i.href}
                  isFooterBlurred
                  as={Link}
                  className="size-48 relative group"
                  href={i.href}
                  shadow="md"
                >
                  <CardHeader className="absolute z-10 top-0">
                    {i.title}
                  </CardHeader>
                  <Image
                    removeWrapper
                    className="z-0 w-full h-full object-cover"
                    src={i.image}
                  />
                  <CardFooter className="absolute bottom-0 transition-transform translate-y-full group-hover:translate-y-0">
                    {i.subtitle}
                  </CardFooter>
                </Card>
              ))}
            </div>
          </Container>
          <div className="max-w-140 mx-auto mt-12 mb-6">
            <Select
              label={t('opening.language')}
              labelPlacement="inside"
              listboxProps={{
                className: 'text-default-700'
              }}
              selectedKeys={[lang]}
              variant="flat"
              onSelectionChange={(keys) => {
                const newLanguage = keys.currentKey;

                if (newLanguage) {
                  setLang(newLanguage);
                  i18n.changeLanguage(newLanguage);
                }
              }}
            >
              <SelectItem key="en">English</SelectItem>
              <SelectItem key="nl">Nederlands</SelectItem>
            </Select>
          </div>
        </div>
      </PaperContainer>
      <Container>
        <div className="max-w-140 mx-auto">
          <div className="py-10">
            <h1 className="text-xl font-bold mb-8">
              {t('opening.about.title')}
            </h1>
            <div className="flex flex-col gap-4">
              <Trans
                components={{
                  p: <p />,
                  // eslint-disable-next-line jsx-a11y/anchor-has-content,jsx-a11y/anchor-is-valid
                  a: <a className="font-bold" />,
                }}
                i18nKey="opening.about.content"
              />
            </div>
          </div>
        </div>
      </Container>
    </FrontPageLayout>
  );
}

export default Opening;
